import { record } from '@rrweb/record'
import { captureFonts, embedImages } from './assets.js'
import { SupportError } from './types.js'
import type { SupportEvent } from './types.js'

// Symbol ownership is shared even when multiple client bundles are loaded in a tab.
const ownershipKey = Symbol.for('aeppic.Support.recorder')
export interface RecorderDriver {
  start(emit: (event: SupportEvent) => void, shares: string[], fail: () => void): (() => void) | Promise<() => void>
  checkpoint(): void
}
export const recorder: RecorderDriver = {
  async start(emit, shares, _fail) {
    const ownership = window as any
    if (ownership[ownershipKey]) throw new SupportError('AlreadyRecording', 'Another Aeppic instance is recording in this tab')
    const sensitive = ':is(.ae-sensitive,[data-ae-sensitive])'
    const allow = shares.length ? `:not(:is(${shares.join(',')}))` : ''
    const blockSelector = `${sensitive}${allow},.rr-block,.ae-support-playback,iframe,object,embed,canvas,input[type="password"],[data-rr-is-password]`
    // Validate before installing any observers. Sensitive markers must be present at first render.
    document.querySelector(blockSelector)
    const passwords = new WeakSet<Element>(Array.from(document.querySelectorAll('input[type="password"],[data-rr-is-password]')))
    const token = {}
    ownership[ownershipKey] = token
    try {
      const fonts = await captureFonts(blockSelector)
      const forward = (event: SupportEvent) => {
        const enrich = (root: any) => embedImages(root, blockSelector)
        if (event.type === 2) {
          enrich(event.data.node)
          const findHead = (n: any): any => n.tagName === 'head' ? n : (n.childNodes || []).map(findHead).find(Boolean)
          const head = findHead(event.data.node)
          if (head && fonts) head.childNodes.push({ type: 2, id: 1000000000, tagName: 'style', attributes: {}, childNodes: [{ type: 3, id: 1000000001, textContent: fonts }] })
        }
        if (event.type === 3 && event.data.source === 0) for (const add of event.data.adds || []) enrich(add.node)
        emit(event)
      }
      const stop = record({
        emit: forward, blockSelector, maskAllInputs: true,
        maskInputFn: (text, element) => shares.length && element?.closest(shares.join(',')) && (element as HTMLInputElement).type !== 'password' && !element.hasAttribute('data-rr-is-password') && !passwords.has(element) ? text : '*'.repeat(text.length),
        recordCanvas: false, recordCrossOriginIframes: false, collectFonts: false, inlineImages: false,
        inlineStylesheet: true, plugins: [],
        // rrweb calls this handler for observer/serialization errors that it
        // can recover from when the handler returns true. Real Aeppic pages
        // contain browser-extension and cross-origin nodes that can throw here
        // even though the initial snapshot and the rest of the recording are
        // valid. Treating every such error as fatal made element captures end
        // immediately with RecordingFailed. The Support lifecycle still
        // rejects a genuinely missing/incomplete initial snapshot.
        errorHandler: error => { console.warn('Support recorder skipped an unreadable page node', error); return true },
      })
      if (!stop) throw new SupportError('RecordingFailed', 'Recorder could not start')
      const imageLoaded = (event: Event) => {
        if (!(event.target instanceof HTMLImageElement)) return
        const id = record.mirror.getId(event.target)
        if (id < 0 || event.target.closest(blockSelector)) return
        const snapshot = { id, tagName: 'img', attributes: {} as Record<string, string> }
        embedImages(snapshot, blockSelector)
        if (snapshot.attributes.rr_dataURL) emit({ type: 3, timestamp: Date.now(), data: { source: 0, adds: [], texts: [], removes: [], attributes: [{ id, attributes: snapshot.attributes }] } })
      }
      document.addEventListener('load', imageLoaded, true)
      const observed = new Set<Element>()
      const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(entries => {
        for (const { target } of entries) {
          const id = record.mirror.getId(target)
          if (id < 0 || !target.isConnected) continue
          const { width, height } = target.getBoundingClientRect()
          emit({ type: 3, timestamp: Date.now(), data: { source: 0, texts: [], removes: [], adds: [],
            attributes: [{ id, attributes: { style: `width:${width}px;height:${height}px;box-sizing:border-box` } }] } })
        }
      })
      const observe = (element: Element) => {
        if (!observed.has(element) && !element.parentElement?.closest(blockSelector)) {
          observed.add(element); resize?.observe(element)
        }
      }
      const scan = (root: Element | Document) => {
        if (root instanceof Element && root.matches('input[type="password"],[data-rr-is-password]')) passwords.add(root)
        root.querySelectorAll('input[type="password"],[data-rr-is-password]').forEach(element => passwords.add(element))
        if (root instanceof Element && root.matches(blockSelector)) observe(root)
        root.querySelectorAll(blockSelector).forEach(observe)
      }
      scan(document)
      const mutations = new MutationObserver(records => {
        for (const mutation of records) for (const added of Array.from(mutation.addedNodes)) if (added instanceof Element) scan(added)
        for (const element of observed) if (!element.isConnected) { resize?.unobserve(element); observed.delete(element) }
      })
      mutations.observe(document, { childList: true, subtree: true })
      return () => {
        try { document.removeEventListener('load', imageLoaded, true); mutations.disconnect(); resize?.disconnect(); observed.clear(); stop() }
        finally { if (ownership[ownershipKey] === token) delete ownership[ownershipKey] }
      }
    } catch (error) { if (ownership[ownershipKey] === token) delete ownership[ownershipKey]; throw error }
  },
  checkpoint() { record.takeFullSnapshot() },
}
